/**
 * §H7 buffer — "point layer select (default wpts) · distance km (1–100)"; result
 * "auto-enable input layer with toast; rings result layer (layer colour, fill .13); summary
 * table (sites, distance, area, reach estimate)".
 *
 * The picker is every vector layer with geometry of its own (`analysisVectors`) rather than
 * points alone: §H7 buffers a `geography`, so a river reach or a settlement extent is as
 * valid an input as a borehole, and the tool already refuses the one row whose features are
 * org units (`wpdens`).
 *
 * **The extent selects features; it does not clip them** (NOTES #604). So the summary names
 * the extent it ran over, and `n_sites` (what went in) is printed beside `n_features` (what
 * came out) — `dissolve` collapses the second and not the first, and a single number would
 * make that look like features had been lost.
 *
 * `notes` are rendered from their **keys** (`reach_is_settlements`, `artifact_truncated`),
 * which is what the API serves; the sentences are this locale file's.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { aoiRing, useToolRun, type BufferResult } from '../../../lib/analysis';
import { fmtKm2, fmtNum } from '../../../lib/format';
import { Icon } from '../../../lib/icons';
import { COUNTRY_NAMES, useAppStore } from '../../../lib/state';
import { toast } from '../../../lib/toast';
import type { ToolProps } from '../AnalysisDock';
import { ToolFrame } from '../ToolFrame';
import { ResultBox, ResultNote } from '../ResultBox';
import {
  analysisVectors,
  DEFAULT_BUFFER_LAYER,
  firstAvailable,
  useAnalysisLayers,
} from '../pickers';

/** `vector.MIN_BUFFER_KM` / `MAX_BUFFER_KM`, and Part H's own default. */
export const MIN_KM = 1;
export const MAX_KM = 100;
export const DEFAULT_KM = 5;

export function Buffer({ onBack }: ToolProps) {
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const aoi = useAppStore((s) => s.aoi);
  const layersOn = useAppStore((s) => s.layersOn);
  const toggleLayer = useAppStore((s) => s.toggleLayer);
  const addResultLayer = useAppStore((s) => s.addResultLayer);

  const layers = useAnalysisLayers();
  const vectors = useMemo(() => analysisVectors(layers), [layers]);
  const [layerId, setLayerId] = useState('');
  const [distance, setDistance] = useState(DEFAULT_KM);
  const [dissolve, setDissolve] = useState(false);
  const [extent, setExtent] = useState<'scope' | 'aoi'>('scope');
  const run = useToolRun<BufferResult>('buffer');

  const current = firstAvailable(vectors, layerId, DEFAULT_BUFFER_LAYER);
  const layer = vectors.find((l) => l.id === current) ?? null;

  const scopeLabel = scope
    ? scope.name
    : country === 'ALL'
      ? t('analysis.change.programme')
      : t(`country.${country}`, COUNTRY_NAMES[country]);

  const submit = () => {
    if (!current) {
      toast(t('analysis.noLayer'));
      return;
    }
    if (distance < MIN_KM || distance > MAX_KM) {
      toast(t('analysis.buffer.range', { min: MIN_KM, max: MAX_KM }));
      return;
    }
    // §H7: "auto-enable input layer with toast" — the rings are meaningless without the
    // sites they were built from.
    if (!layersOn.has(current)) {
      toggleLayer(current, true);
      toast(t('analysis.buffer.switchedOn', { layer: layer?.name ?? current }));
    }
    run.run({
      layer_id: current,
      distance_km: distance,
      dissolve,
      ...(extent === 'aoi' && aoi
        ? { aoi_polygon: aoiRing(aoi.pts) }
        : scope
          ? { unit_id: scope.unitId }
          : country === 'ALL'
            ? {}
            : { country }),
    });
  };

  const result = run.result;

  const addRings = () => {
    const spec = result?.result_layers?.[0];
    if (!spec) return;
    addResultLayer(spec, { jobId: run.jobId ?? spec.id, tool: 'buffer' });
    toast(t('analysis.buffer.added'));
  };

  return (
    <ToolFrame
      tool="buffer"
      onBack={onBack}
      phase={run.phase}
      progress={run.progress}
      error={run.error}
      cached={run.cached}
      result={
        result ? (
          <>
            <ResultBox title={t('analysis.buffer.title', { layer: result.layer.name })}>
              <table className="mini">
                <tbody>
                  <tr>
                    <td>{t('analysis.buffer.sites')}</td>
                    <td className="num">{fmtNum(result.n_sites, 0)}</td>
                  </tr>
                  <tr>
                    <td>{t('analysis.buffer.features')}</td>
                    <td className="num">{fmtNum(result.n_features, 0)}</td>
                  </tr>
                  <tr>
                    <td>{t('analysis.buffer.distance')}</td>
                    <td className="num">{fmtNum(result.distance_km, 0)} km</td>
                  </tr>
                  <tr>
                    <td>{t('analysis.buffer.area')}</td>
                    <td className="num">{fmtKm2(result.buffered_km2, 1)}</td>
                  </tr>
                  <tr>
                    <td>{t('analysis.buffer.settlements')}</td>
                    <td className="num">{fmtNum(result.features_within.settlements, 0)}</td>
                  </tr>
                  <tr>
                    <td>{t('analysis.buffer.sitesWithin')}</td>
                    <td className="num">{fmtNum(result.features_within.sites, 0)}</td>
                  </tr>
                </tbody>
              </table>
            </ResultBox>

            <ResultNote>
              {t('analysis.buffer.extentNote', { extent: result.extent.label })}
              {result.dissolve ? ` ${t('analysis.buffer.dissolved')}` : ` ${t('analysis.buffer.overlaps')}`}
            </ResultNote>
            {(result.notes ?? []).map((note) => (
              <ResultNote key={note}>{t(`analysis.note.${note}`, note)}</ResultNote>
            ))}

            {result.result_layers.length ? (
              <div className="ad-actions">
                <button type="button" className="btn primary sm" id="bfMap" onClick={addRings}>
                  <Icon name="map" size={12} /> {t('analysis.buffer.show')}
                </button>
              </div>
            ) : null}
          </>
        ) : null
      }
    >
      <label className="fld" htmlFor="bfLayer">
        {t('analysis.buffer.layer')}
      </label>
      <select className="std" id="bfLayer" value={current} onChange={(e) => setLayerId(e.target.value)}>
        {vectors.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
        {vectors.length === 0 ? <option value="">{t('analysis.noLayerOption')}</option> : null}
      </select>

      <label className="fld" htmlFor="bfDist">
        {t('analysis.buffer.distanceField', { min: MIN_KM, max: MAX_KM })}
      </label>
      <input
        type="number"
        className="std"
        id="bfDist"
        min={MIN_KM}
        max={MAX_KM}
        value={distance}
        onChange={(e) => setDistance(Number(e.target.value))}
      />

      <label className="fld" htmlFor="bfExt">
        {t('analysis.change.extent')}
      </label>
      <select
        className="std"
        id="bfExt"
        value={extent}
        onChange={(e) => setExtent(e.target.value as 'scope' | 'aoi')}
      >
        <option value="scope">{scopeLabel}</option>
        {aoi ? <option value="aoi">{t('analysis.aoiName')}</option> : null}
      </select>

      <div className="ad-checks" style={{ marginTop: 8 }}>
        <label>
          <input type="checkbox" id="bfDissolve" checked={dissolve} onChange={() => setDissolve((d) => !d)} />
          {t('analysis.buffer.dissolve')}
        </label>
      </div>

      <button
        type="button"
        className="btn primary ad-run"
        id="bfRun"
        disabled={run.phase === 'running'}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('analysis.buffer.run')}
      </button>
    </ToolFrame>
  );
}

export default Buffer;
